import {
	ActionListItem,
	DropOverActionList,
	IconButton
} from "client/Components";
import {
	AppIntegrationFlowValidationResultSampleFieldModel
} from "client/Api/IntegrationBuilder/integrationBuilderFlows";
import {
	IInputAttachable,
	IObjectInputAttachable,
	IValueInputAttachable
} from "client/Forms/attachable";
import {
	ensureNotNil,
	generateTrackingId
} from "client/Misc/utils";
import {
	PropsWithChildren,
	ReactElement,
	RefObject,
	useCallback,
	useRef,
	useState
} from "react";
import { ExpressionTextInput } from "./ExpressionTextInput";
import { ResultMapperFormResult } from "./ResultMapperFormResult";
import { localize } from "client_legacy/ndaw/culture";
import styles from "./AppResultFieldWithColorAction.module.less";
import { createDefaultColorCondition } from "./ColorSelector";
import {
	ResultMapperColorConditionModelFormData,
	ResultMapperStyledFieldFormData
} from "./common";

type AppResultFieldWithColorActionProps<TResultValue> = {
	attachable: IObjectInputAttachable<ResultMapperStyledFieldFormData>;
	hasResult: boolean;
	isReadOnly: boolean;
	resultField?: AppIntegrationFlowValidationResultSampleFieldModel<TResultValue> | null;
};

export const AppResultFieldWithColorActions = <TResultValue,>(
	props: PropsWithChildren<AppResultFieldWithColorActionProps<TResultValue>>
): ReactElement => {
	const {
		attachable,
		hasResult,
		isReadOnly,
		children,
		resultField
	} = props;

	const colorsAttachable: IValueInputAttachable<readonly ResultMapperColorConditionModelFormData[]> = attachable.for("colors");
	const includeColorsAttachable: IInputAttachable<boolean> = attachable.for("shouldIncludeColors");

	const [isActionsOpen, setIsActionsOpen] = useState<boolean>(false);
	const actionRef: RefObject<HTMLButtonElement | null> = useRef<HTMLButtonElement>(null);

	const hasColorsSection: boolean = includeColorsAttachable.value;

	const setColorsHandler = useCallback(
		(): void => {
			includeColorsAttachable.reducer
				.setValue(true);

			const colorAttachableValue: readonly ResultMapperColorConditionModelFormData[] = colorsAttachable.value;

			if (colorAttachableValue.length === 0) {
				colorsAttachable.reducer
					.setValue([createDefaultColorCondition()]);
			}

			colorsAttachable.validation
				.triggerInputFinished();

			setIsActionsOpen(false);
		},
		[
			includeColorsAttachable,
			colorsAttachable
		]
	);

	const removeColorsHandler = useCallback(
		(): void => {
			includeColorsAttachable.reducer
				.setValue(false);

			colorsAttachable.validation
				.triggerInputFinished();

			setIsActionsOpen(false);
		},
		[
			colorsAttachable.validation,
			includeColorsAttachable.reducer
		]
	);

	const expressionAttachable: IInputAttachable<string> = attachable.for("expression");

	return (
		<>
			<div className={styles.inputAndActions}>
				<div className={styles.input}>
					<ExpressionTextInput
						isReadOnly={isReadOnly}
						attachable={expressionAttachable}
					/>
				</div>
				{!isReadOnly && (
					<>
						<IconButton
							icon="ellipsis"
							kind="secondary"
							onClick={() => setIsActionsOpen(true)}
							trackingId={generateTrackingId(expressionAttachable.reducer.path, "_actions")}
							ref={actionRef}
						/>
						{isActionsOpen && (
							<DropOverActionList
								target={ensureNotNil(actionRef.current)}
								targetAlignment="br"
								contentAlignment="tr"
								onClose={() => setIsActionsOpen(false)}
								menuTitle={localize("_Actions")}
								trackingId={generateTrackingId("actions", "__dropover")}
							>
								{hasColorsSection
									? (
										<ActionListItem
											icon="xmark"
											text={localize("_RemoveColors")}
											trackingId="remove-colors"
											onClick={removeColorsHandler}
										/>
									)
									: (
										<ActionListItem
											icon="palette"
											text={localize("_SetColors")}
											trackingId="set-colors"
											onClick={setColorsHandler}
										/>
									)
								}
							</DropOverActionList>
						)}
					</>
				)}
			</div>

			<ResultMapperFormResult
				attachable={expressionAttachable}
				hasResult={hasResult}
				resultValue={resultField?.value}
			>
				{children}
			</ResultMapperFormResult>
		</>
	);
};
AppResultFieldWithColorActions.displayName = "AppResultFieldWithColorActions";
